<template>
  <div class="upload-container" :class="{'simple': simple}">
    <!-- 已上传的图片 -->
    <div v-if="imageUrl" class="image-preview">
      <img :src="imageUrl" class="uploaded-image" />
      <div class="image-actions">
        <i class="el-icon-view" @click="previewImage"></i>
        <i class="el-icon-edit" @click="triggerUpload"></i>
        <i class="el-icon-delete" @click="removeImage"></i>
      </div>
    </div>
    
    <!-- 上传按钮 -->
    <div 
      v-else
      v-loading="uploading"
      class="upload-trigger"
      @click="triggerUpload"
    >
      <i class="el-icon-plus upload-icon"></i>
      <div class="upload-text" v-if="!simple">点击上传</div>
    </div>
    
    <!-- 隐藏的文件输入框 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileChange" 
      accept="image/*" 
      style="display: none;"
    />
  </div>
</template>

<script>
export default {
  name: 'UploadImage',
  props: {
    value: {
      type: String,
      default: ''
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: '100px'
    },
    simple: {
      type: Boolean,
      default: false
    },    
  },
  data() {
    return {
      uploading: false,
      imageUrl: this.value
    };
  },
  watch: {
    value(newVal) {
      this.imageUrl = newVal;
    },
    imageUrl(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    // 触发文件选择
    triggerUpload() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    
    // 处理文件选择
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // 验证文件
      if (!this.validateFile(file)) {
        // 清空input
        this.$refs.fileInput.value = '';
        return;
      }
      
      try {
        await this.uploadFile(file);
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error('上传失败，请重试');
      } finally {
        // 清空input以便重新选择同一文件
        this.$refs.fileInput.value = '';
      }
    },
    
    // 文件验证
    validateFile(file) {
      // 文件类型验证
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        this.$message.error('只支持上传 jpg、jpeg、png、gif 格式的图片');
        return false;
      }
      
      // 文件大小验证 (限制1MB)
      // const isLt1M = file.size / 1024 / 1024 < 1;
      // if (!isLt1M) {
      //   this.$message.error('上传图片大小不能超过 1MB');
      //   return false;
      // }
      
      return true;
    },
    
    // 上传文件
    async uploadFile(file) {
      this.uploading = true;
      
      try {
        // 生成文件名（与原始组件完全一致）
        const fileName = this.generateFileName(file.name);
        
        // 使用S3上传（与原始组件相同的方法）
        const imageUrl = await this.uploadS3Img(file, fileName);
        
        // 设置图片URL
        this.imageUrl = imageUrl;
        this.$message.success('图片上传成功');
        
      } catch (error) {
        console.error('上传失败:', error);
        this.$message.error('上传失败，请重试');
      } finally {
        this.uploading = false;
      }
    },
    
    // S3上传（与原始组件完全相同的实现）
    async uploadS3Img(file, fileName) {
      // 获取S3配置
      const { data: resData } = await this.$get("s3/config/info", {});
      if (resData.result !== 200) {
        throw new Error('获取S3配置失败');
      }
      
      const s3Config = resData.data;
      
      // S3配置（与原始组件相同）
      const accountid = "05e3a3acf3a5440a0f6aa4e94818fac2";
      const accessKeyId = "adfaeaebf3a0adc777a2365aa4f7500a";
      const accessKey = "93f858b5e3e7d01f34dc25fcd0e228c61124872996059c2ec464a0431e0355b4";
      
      const s3 = new AWS.S3({
        params: { Bucket: "static-taotan" },
        endpoint: `https://${accountid}.r2.cloudflarestorage.com`,
        accessKeyId: `${accessKeyId}`,
        secretAccessKey: `${accessKey}`,
        signatureVersion: "v4",
      });
      
      const albumPhotosKey = "micros/";
      const photoKey = albumPhotosKey + fileName;
      
      return new Promise((resolve, reject) => {
        s3.upload(
          {
            Key: photoKey,
            Body: file,
          },
          (error, data) => {
            if (error) {
              console.error('S3上传错误:', error);
              reject(error);
            } else {
              const domain = s3Config.domain;
              const imgurl = `${domain}/${data.key}`;
              resolve(imgurl);
            }
          }
        );
      });
    },
    
    // 生成文件名（与原始组件完全一致）
    generateFileName(originalName) {
      const data = new Date();
      const year = data.getFullYear().toString();
      const month = data.getMonth() + 1;
      const timestamp = year + month + data.getDate() + data.getHours() + data.getMinutes() + data.getSeconds() + data.getMilliseconds();
      
      // 获取文件扩展名（与原始组件的getCaption方法一致）
      const index = originalName.lastIndexOf(".");
      const extension = originalName.substring(index + 1, originalName.length);
      
      return `${timestamp}.${extension}`;
    },
    
    // 预览图片
    previewImage() {
      // 可以实现图片预览功能
      window.open(this.imageUrl, '_blank');
    },
    
    // 删除图片
    removeImage() {
      this.imageUrl = '';
    }
  }
};
</script>

<style scoped>
.upload-container {
  display: inline-block;
  width: v-bind(size);
  height: v-bind(size);
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  &.simple{
    .image-actions{
      gap: 4px;
      i{
        font-size: 16px;
      }
    }
    .upload-icon{
      margin-bottom: 0;
    }
    
  }
}

.upload-container:hover {
  border-color: #409EFF;
}

.upload-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
}

.upload-icon {
  font-size: 28px;
  color: #8c939d;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 12px;
  color: #8c939d;
}

.image-preview {
  width: 100%;
  height: 100%;
  position: relative;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.image-preview:hover .image-actions {
  opacity: 1;
}

.image-actions i {
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
}

.image-actions i:hover {
  color: #409EFF;
}
</style> 